/**
 * SETTINGS PANEL COMPONENT
 * Application settings and preferences
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Settings, Brain, Palette, Globe } from 'lucide-react';

export function SettingsPanel() {
  const [aiModel, setAiModel] = React.useState('llama-3.3-70b');
  const [responseStyle, setResponseStyle] = React.useState('detailed');
  const [theme, setTheme] = React.useState('dark');
  const [language, setLanguage] = React.useState('javascript');

  const handleSaveSettings = () => {
    const settings = { aiModel, responseStyle, theme, language };
    localStorage.setItem('codemind-settings', JSON.stringify(settings));
    alert('Settings saved successfully!');
  };

  return (
    <div className="h-full p-6 bg-gradient-to-b from-slate-900/50 to-slate-800/50 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Settings className="w-8 h-8 text-blue-400" />
            <span>Settings</span>
          </h1>
          <p className="text-slate-400">Customize your CodeMind AI experience</p>
        </div>

        <div className="space-y-6">
          {/* AI Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Brain className="w-5 h-5 text-purple-400" />
              <span>AI Configuration</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">AI Model</label>
                <select 
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                >
                  <option value="llama-3.3-70b">Llama 3.3 70B (Recommended)</option>
                  <option value="llama-3.1-8b">Llama 3.1 8B (Faster)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">Response Style</label>
                <select 
                  value={responseStyle}
                  onChange={(e) => setResponseStyle(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                >
                  <option value="detailed">Detailed Explanations</option>
                  <option value="concise">Concise Answers</option>
                  <option value="code-focused">Code-Focused</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Appearance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Palette className="w-5 h-5 text-pink-400" />
              <span>Appearance</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Theme</label>
                <div className="flex space-x-3">
                  <button 
                    onClick={() => setTheme('dark')}
                    className={`px-4 py-2 border rounded ${theme === 'dark' ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white/10 border-white/20 text-slate-300'}`}
                  >
                    Dark
                  </button>
                  <button 
                    onClick={() => setTheme('light')}
                    className={`px-4 py-2 border rounded ${theme === 'light' ? 'bg-white border-gray-300 text-black' : 'bg-white/10 border-white/20 text-slate-300'}`}
                  >
                    Light
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Language */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Globe className="w-5 h-5 text-green-400" />
              <span>Language & Region</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Preferred Programming Language</label>
                <select 
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                >
                  <option value="javascript">JavaScript</option>
                  <option value="typescript">TypeScript</option>
                  <option value="python">Python</option>
                  <option value="go">Go</option>
                  <option value="rust">Rust</option>
                </select>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <button
            onClick={handleSaveSettings}
            className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105"
          >
            Save Settings
          </button>
        </motion.div>
      </div>
    </div>
  );
}